import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { listContracts } from '@/api/contracts';
import { useAuth } from '@/auth/useAuth';
import { contractTypes } from './schema';
import { formatDate } from '@/lib/dateFormat';
import { useSort, sortRows, matchesQuery } from '@/lib/sortTable';
import { SortHeader } from '@/components/ui/SortHeader';
import { DesktopTable, MobileCards, MobileCard, CardField } from '@/components/common/responsiveList';
import type { Contract, ContractStatus } from '@/types/oscar';

const statusBadge: Record<ContractStatus, string> = {
  Active: 'bg-green/15 text-green',
  ExpiringSoon: 'bg-orange/15 text-orange',
  Expired: 'bg-red/15 text-red',
  Cancelled: 'bg-darkGrey/10 text-darkGrey',
};

export function ContractsRegister() {
  const { canMutate } = useAuth();
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [expiring, setExpiring] = useState(false);
  const [search, setSearch] = useState('');
  const { sortKey, sortDir, onSort } = useSort('name');

  const query = useQuery({
    queryKey: ['contracts', { type, expiring }],
    queryFn: ({ signal }) => listContracts({ type, expiringDays: expiring ? 90 : undefined }, signal),
  });

  const colValue = (c: Contract, k: string): unknown =>
    ({ name: c.ContractName, vendor: c.Vendor, type: c.ContractType, endDate: c.EndDate, notice: c.NoticeDate, status: c.Status } as Record<string, unknown>)[k];
  const statusOpts = [...new Set((query.data ?? []).map((c) => c.Status).filter(Boolean))].sort();
  const rows = sortRows(
    (query.data ?? []).filter((c) => matchesQuery(search, [c.ContractName, c.Vendor, c.ContractType, c.Status]) && (!status || c.Status === status)),
    (c) => colValue(c, sortKey),
    sortDir,
  );

  return (
    <section>
      <header className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Contracts</h1>
          <p className="mt-1 text-sm text-darkGrey/80">Hardware leases, software EAs, telco and infrastructure agreements.</p>
        </div>
        {canMutate && (
          <Link to="/contracts/new" className="inline-flex items-center gap-2 rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90">
            <FileText className="h-4 w-4" aria-hidden /> Add contract
          </Link>
        )}
      </header>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Type
          <select aria-label="Filter by type" value={type} onChange={(e) => setType(e.target.value)} className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
            <option value="">All</option>
            {contractTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Status
          <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
            <option value="">All</option>
            {statusOpts.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-xs font-semibold text-darkGrey">
          <input type="checkbox" aria-label="Expiring soon" checked={expiring} onChange={(e) => setExpiring(e.target.checked)} />
          Expiring ≤ 90 days
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search
          <input aria-label="Search contracts" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, vendor, type or status" className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
        </label>
      </div>

      <DesktopTable className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr>
              <SortHeader label="Name" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Vendor" columnKey="vendor" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Type" columnKey="type" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="End date" columnKey="endDate" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Notice" columnKey="notice" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Status" columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {query.isLoading && <tr><td colSpan={6} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>}
            {query.isError && <tr><td colSpan={6} className="px-3 py-6 text-center text-red">Could not load contracts.</td></tr>}
            {rows.length === 0 && !query.isLoading && <tr><td colSpan={6} className="px-3 py-6 text-center text-darkGrey/60">No contracts match the filters.</td></tr>}
            {rows.map((c) => (
              <tr key={c.ContractId} className="border-t border-border hover:bg-bg/60">
                <td className="px-3 py-2"><Link to={`/contracts/${c.ContractId}`} className="font-medium text-navy hover:underline">{c.ContractName}</Link></td>
                <td className="px-3 py-2 text-darkGrey/80">{c.Vendor}</td>
                <td className="px-3 py-2 text-darkGrey/80">{c.ContractType}</td>
                <td className="px-3 py-2 text-darkGrey/80">{formatDate(c.EndDate)}</td>
                <td className="px-3 py-2 text-darkGrey/80">{formatDate(c.NoticeDate)}</td>
                <td className="px-3 py-2"><span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadge[c.Status]}`}>{c.Status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </DesktopTable>

      {/* Mobile (<md): contracts as stacked cards. */}
      <MobileCards>
        {query.isLoading && <p className="px-1 py-6 text-center text-sm text-darkGrey/60">Loading…</p>}
        {query.isError && <p className="px-1 py-6 text-center text-sm text-red">Could not load contracts.</p>}
        {rows.length === 0 && !query.isLoading && (
          <p className="px-1 py-6 text-center text-sm text-darkGrey/60">No contracts match the filters.</p>
        )}
        {rows.map((c) => (
          <MobileCard key={c.ContractId}>
            <div className="flex items-start justify-between gap-2">
              <Link to={`/contracts/${c.ContractId}`} className="text-sm font-semibold text-navy hover:text-orange hover:underline">
                {c.ContractName}
              </Link>
              <span className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadge[c.Status]}`}>{c.Status}</span>
            </div>
            <div className="mt-2 border-t border-border pt-2">
              <CardField label="Vendor">{c.Vendor}</CardField>
              <CardField label="Type">{c.ContractType}</CardField>
              <CardField label="End date">{formatDate(c.EndDate)}</CardField>
              <CardField label="Notice">{formatDate(c.NoticeDate)}</CardField>
            </div>
          </MobileCard>
        ))}
      </MobileCards>
    </section>
  );
}
